/**
 * world_look — what each KIND of world looks like (the natural object layer, 2026-10-07). PURE: no three, no DOM.
 *
 * `system_makeup.ts` says WHAT a system holds and WHERE; this file says how each kind is drawn — which pooled face a
 * planet wears and what is done to it (planet_look.ts reads `PlanetSurface`), what a moon is, how a comet's two tails
 * point, what a belt's rock is made of, how dense a disc is at a radius. `verify:natural-worlds` pins every table and
 * formula here; the TSL in planet_look / comet_view / disc_system mirrors them.
 *
 * TRUE COLOUR (the catalog §1.1, §13.4): the values are what an eye would see, not a false-colour press image — Venus is
 * a featureless pale yellow-white, the two ice giants are nearly the same pale greenish-blue (Irwin et al. 2024), a hot
 * Jupiter is as likely coal-black with a dull red glow as it is blue, a comet's nucleus is among the darkest things in a
 * system. Sources per value are in docs/research/2026-10-07-space-objects/impl-W.md.
 */
import type { PlanetKind, PlanetMakeup, MoonKind, MoonMakeup, FieldTaxonomy, FieldLandmark, OrbitMakeup } from './natural_types.js';
import { orbitAt, type V3 } from './natural_orbit.js';
import { DEPTH_CODE } from '../space_look.js';
import { planetFaceAt, moonFaceAt, type FaceRow } from './face_catalog.js';

// ── hashing ─────────────────────────────────────────────────────────────────────────────────────────────────────
/** fnv-1a + murmur3's fmix32 (THE HASH LAW: bare FNV is arithmetic in a key's last character). */
export function mix32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
export const mix01 = (s: string): number => mix32(s) / 4294967296;
const pickOf = <T>(list: ReadonlyArray<T>, u: number): T => list[Math.min(list.length - 1, Math.floor(u * list.length))];
const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const smooth = (a: number, b: number, x: number): number => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
const fin = (v: number, d: number): number => (Number.isFinite(v) ? v : d);

// ── the pooled faces ────────────────────────────────────────────────────────────────────────────────────────────
/** Index into space_sector.WORLD_PLANET_POOL. */
export const FACE = { desert: 0, gas: 1, ice: 2, moon: 3, ocean: 4 } as const;
/** Each face's mean colour (sRGB 0..1, measured off the served 4K equirects) and its mean LINEAR luminance. */
export const FACE_MEAN: ReadonlyArray<{ rgb: [number, number, number]; lum: number }> = [
  { rgb: [0.580, 0.427, 0.337], lum: 0.170 },  // desert: butterscotch with dark provinces, white caps
  { rgb: [0.855, 0.655, 0.333], lum: 0.410 },  // gas: gold bands with one pale oval
  { rgb: [0.667, 0.780, 0.812], lum: 0.540 },  // ice: pale cyan plates, a darker equatorial lead
  { rgb: [0.573, 0.561, 0.545], lum: 0.275 },  // moon: grey highlands, dark maria
  { rgb: [0.341, 0.467, 0.596], lum: 0.180 },  // ocean: deep blue, olive land, white swirls and caps
];

// ── what planet_look.ts draws on top of a face ──────────────────────────────────────────────────────────────────
/** The kind's own surface terms (all optional; colours sRGB hex; angles rad; directions in the body's own frame, y = its spin axis). */
export interface PlanetSurface {
  /** Pull the face toward ONE colour (a cloud deck, a haze): `amount` 0..1; `keep` = how much of the face's own light-and-dark survives (0 = none); `mean` = that face's mean linear luminance. */
  /** `mean` = the mean linear luminance of the face being WORN; `poolMean` = the pooled face's, kept when a catalog face is
   *  worn so that THE FALLBACK (the pooled face shown instead) is measured against its own mean again (PlanetLook.wearPooled). */
  flatten?: { color: number; amount: number; keep: number; mean: number; poolMean?: number };
  /** The ocean face's land drowned (a world with no continents). */
  drown?: { sea: number };
  /** Broad low-frequency provinces of another colour (sulphur fields, tholin stains). */
  blotch?: { color: number; amount: number; scale: number; seed: number };
  /** Latitude bands multiplied over the face: `dark` / `light` are the band colours' multipliers, `count` bands pole to pole. */
  bands?: { dark: number; light: number; count: number; contrast: number; warp: number; seed: number };
  /** One great oval storm. */
  storm?: { lat: number; lon: number; size: number; color: number; amount: number };
  /** A network of thin lines in the albedo (an ice shell's lineae; `polar` > −1 keeps them south of that sin-latitude). */
  lines?: { color: number; amount: number; scale: number; width: number; seed: number; polar: number };
  /** A polar cap (both poles, or one). */
  cap?: { lat: number; color: number; soft: number; north: boolean; south: boolean };
  /** A broad specular lobe on bright ice. */
  gloss?: { strength: number; roughness: number };
  /**
   * Molten rock. The crust is cracked into plates whose rifts glow, hottest under the star (`night` = the share of that
   * heat left on the night side; 1 = the body's own heat, the same everywhere). `seas` 0..1 = how far a magma ocean
   * reaches from the sub-stellar point (0 = none); `spots` 0..1 = scattered hot spots (calderas), none when absent.
   */
  lava?: { hot: number; cool: number; gain: number; scale: number; width: number; seas: number; night: number; seed: number; spots?: number };
  /** Thermal glow of a gas envelope: the day side (a hot spot `shift` rad east of the sub-stellar point), the night side, or the whole body. */
  glow?: { day?: { color: number; gain: number; power: number; shift: number }; night?: { color: number; gain: number }; all?: { color: number; gain: number } };
  /** Auroral ovals round a tilted magnetic axis (both poles). */
  aurora?: { color: number; color2: number; gain: number; colat: number; width: number; tilt: number; lon: number; seed: number };
  /** A haze-soft limb: the lit colour goes to `color` toward the edge of the disc. */
  limb?: { color: number; amount: number; power: number };
  /** × the star's light on this body (a starless world: a few per cent). */
  sun?: number;
}
/** Options planet_look.buildAtmosphereShell takes beyond its hue (absent = the stock O'Neil shell at 1.025 R). */
export interface ShellExtra {
  /** The shell's outer radius in planet radii. */
  outer?: number;
  /** R, G, B "wavelengths" (µm) — scattering goes as λ⁻⁴ (`shellWavelengths` turns a colour into them). */
  wavelengths?: [number, number, number];
  intensity?: number;
  eSun?: number;
  km?: number;
  /**
   * An EXTENDED haze instead (O'Neil's fit holds at 1.025 R only): its colour, scale height in planet radii, the
   * optical depth along a ray that grazes the surface, and a gain. `outer` is then where the haze ends.
   */
  haze?: { color: number; scale: number; tau: number; gain: number; outer?: number };
}
/** O'Neil's shell scatters as λ⁻⁴: the three wavelengths that make its limb the colour `rgb` (linear, 0..1). */
export function shellWavelengths(rgb: readonly [number, number, number], base = 0.475): [number, number, number] {
  const mx = Math.max(rgb[0], rgb[1], rgb[2], 1e-3);
  const f = (c: number): number => base * Math.pow(mx / Math.max(c, 0.02), 0.25);
  return [f(rgb[0]), f(rgb[1]), f(rgb[2])];
}
export const hexRgb = (hex: number): [number, number, number] => [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
const srgbToLin = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
export const hexLinear = (hex: number): [number, number, number] => { const c = hexRgb(hex); return [srgbToLin(c[0]), srgbToLin(c[1]), srgbToLin(c[2])]; };
const hueOf = (hex: number): number => {
  const [r, g, b] = hexRgb(hex); const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (d < 1e-6) return 0;
  const h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return ((h / 6) % 1 + 1) % 1;
};

/** A planet kind's look, three-free (worlds_view turns it into natural_api.PlanetKindLook). */
export interface PlanetKindSpec {
  kind: PlanetKind;
  /** The variant the seed picked (telemetry, the bed's captions). */
  variant: string;
  /** The POOLED face (space_sector.WORLD_PLANET_POOL) — what the kind's terms were written over, and the fallback. */
  face: number;
  /** A catalog face (face_catalog.ts) worn instead when it loads; null = the pooled one. */
  faceKey: string | null;
  tint: number;
  rim: number;
  atmosphere: number;
  ocean: boolean;
  surface: PlanetSurface | null;
  /** null = airless: no shell at all. */
  shell: { hue: number; extra: ShellExtra } | null;
  /** rad/s about the body's y (0 = tidally locked: the same face to its star). */
  spin: number;
  /** The mean colour an eye sees on the day side (sRGB 0..1) — the harness's "are the kinds different" measure. */
  mean: [number, number, number];
}
const shellOf = (hex: number, extra: Omit<ShellExtra, 'wavelengths'> = {}): { hue: number; extra: ShellExtra } => ({ hue: hueOf(hex), extra: { ...extra, wavelengths: shellWavelengths(hexLinear(hex)) } });
/** A shell that reaches past O'Neil's 2.5 %: five scale heights inside `outer`. */
const hazeOf = (hex: number, outer: number, tau: number, gain: number, thin?: number): { hue: number; extra: ShellExtra } => ({ hue: hueOf(hex), extra: { outer, haze: { color: hex, scale: (outer - 1) / 5, tau, gain, ...(thin !== undefined ? { outer: thin } : {}) } } });
const mulHex = (a: readonly [number, number, number], hex: number): [number, number, number] => { const c = hexRgb(hex); return [a[0] * c[0], a[1] * c[1], a[2] * c[2]]; };
const mixRgb = (a: readonly [number, number, number], b: readonly [number, number, number], t: number): [number, number, number] => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
/** The day side's mean colour: the face × tint, pulled toward the flatten colour (the bands and provinces average out). */
function meanOf(face: number, tint: number, surface: PlanetSurface | null, worn?: readonly [number, number, number]): [number, number, number] {
  let c = mulHex(worn ?? FACE_MEAN[face].rgb, tint);
  if (surface?.drown) c = mixRgb(c, hexRgb(surface.drown.sea), 0.3);
  if (surface?.flatten) c = mixRgb(c, hexRgb(surface.flatten.color), surface.flatten.amount);
  if (surface?.blotch) c = mixRgb(c, hexRgb(surface.blotch.color), surface.blotch.amount * 0.35);
  return c;
}

/** Options of the kind specs: `faces: false` = the five pooled faces alone (`atlas:planet-faces = 'off'`, the bed's before-leg). */
export interface KindOpts { faces?: boolean }
/** THE MEAN TRAVELS WITH THE FACE: a kind's `flatten` measures the face's detail against that face's own mean luminance. */
function wearFace(surface: PlanetSurface | null, row: FaceRow | null): PlanetSurface | null {
  return row && surface?.flatten ? { ...surface, flatten: { ...surface.flatten, mean: row.lum, poolMean: surface.flatten.mean } } : surface;
}

/**
 * THE THIRTEEN KINDS. The planet's radius, place and makeup kind are the makeup's; the face is ours to choose (the
 * makeup's own pooled face decided the kind, not the picture). Every seeded choice goes through mix01 (THE HASH LAW) —
 * the catalog face too (`face_catalog.PLANET_FACES[kind]` by the planet's own seed: two lava worlds differ).
 */
export function planetKindSpec(p: Pick<PlanetMakeup, 'kind' | 'seed' | 'heat' | 'aurora'> & { superEarth?: boolean }, key = '', opt: KindOpts = {}): PlanetKindSpec {
  const u = (salt: string): number => mix01(`${key}:pk:${p.seed}:${salt}`);
  const seed = p.seed % 997;
  const spinDir = u('sd') < 0.12 ? -1 : 1; // a few turn backwards
  let face: number = FACE.moon, tint = 0xffffff, rim = 0x998877, atmosphere = 0, ocean = false, variant = '', spin = 0.004;
  let surface: PlanetSurface | null = null;
  let shell: PlanetKindSpec['shell'] = null;
  switch (p.kind) {
    case 'airless': {
      // PL-01 — the Moon / Mercury: bare regolith, a hard terminator, no limb glow (geometric albedo ~0.12)
      const v = pickOf([['lunar grey', 0xc4c0ba], ['brown-grey', 0xb8a898], ['dark basalt', 0x9a9690], ['pale regolith', 0xd6d0c6]] as const, u('v'));
      face = FACE.moon; variant = v[0]; tint = v[1]; rim = 0x8a8680; atmosphere = 0; spin = 0.0015 + u('s') * 0.003;
      break;
    }
    case 'desert': {
      // PL-02 — Mars from orbit is butterscotch and ochre under a thin dusty limb (Rosetta OSIRIS 2007); not every desert is orange
      const v = pickOf([['butterscotch', 0xffffff, 0xe9b98c], ['pale ochre', 0xf2e0c0, 0xe6caa0], ['rust', 0xf0c0a8, 0xe0a080], ['grey-mauve dust', 0xcfc6d2, 0xcfc0c8], ['umber', 0xc8b090, 0xd0a878]] as const, u('v'));
      face = FACE.desert; variant = v[0]; tint = v[1]; rim = v[2]; atmosphere = 0.3; spin = 0.004 + u('s') * 0.002;
      shell = shellOf(v[2], { intensity: 0.55 });
      break;
    }
    case 'temperate': {
      // PL-03 — oceans, coasts, cloud systems, ice caps; a thin blue limb and the sun's glint on the sea
      const v = pickOf([['blue marble', 0xffffff], ['green seas', 0xeafff0], ['warm seas', 0xfff3e6]] as const, u('v'));
      face = FACE.ocean; variant = v[0]; tint = v[1]; rim = 0x7ec8ff; atmosphere = 1.0; ocean = true; spin = 0.004 + u('s') * 0.002;
      shell = { hue: 0.58, extra: {} };
      break;
    }
    case 'water': {
      // PL-04 — no continents: one ocean under weather, the strongest glint of any kind
      const v = pickOf([['deep blue', 0x173f7a, 0x8fd0ff], ['teal', 0x14586e, 0x8fe0e8], ['slate sea', 0x24456e, 0xa0c8f0]] as const, u('v'));
      face = FACE.ocean; variant = v[0]; tint = 0xffffff; rim = v[2]; atmosphere = 1.1; ocean = true; spin = 0.0035 + u('s') * 0.002;
      surface = { drown: { sea: v[1] } };
      shell = shellOf(v[2], { intensity: 1.05 });
      break;
    }
    case 'greenhouse': {
      // PL-05 — Venus in true colour: a bright pale yellow-white cloud deck, "nearly as featureless as a cue ball"
      const v = pickOf([['pale yellow', 0xf2e6b8, 0xfff2c8], ['cream', 0xf2ead2, 0xfff6e0], ['sulphur haze', 0xe8d498, 0xf6e2a8]] as const, u('v'));
      face = FACE.ocean; variant = v[0]; tint = 0xffffff; rim = v[2]; atmosphere = 1.3; spin = 0.0006 * spinDir;
      surface = { flatten: { color: v[1], amount: 0.92, keep: 0.13, mean: FACE_MEAN[FACE.ocean].lum }, limb: { color: v[2], amount: 0.35, power: 3 } };
      shell = hazeOf(v[2], 1.035, 1.6, 0.8);
      break;
    }
    case 'frozen': {
      // PL-06 — a snowball: white-blue ice of high albedo with dark leads, a soft sheen where the star reflects
      const v = pickOf([['white ice', 0xf2f6fa, 0.5], ['blue ice', 0xdcecf6, 0.3], ['stained ice', 0xeee6da, 0.45]] as const, u('v'));
      face = FACE.ice; variant = v[0]; tint = 0xffffff; rim = 0xcfe8ff; atmosphere = 0.25; spin = 0.003 + u('s') * 0.002;
      surface = { flatten: { color: v[1], amount: v[2], keep: 0.9, mean: FACE_MEAN[FACE.ice].lum }, gloss: { strength: 0.55, roughness: 0.32 } };
      shell = shellOf(0xcfe8ff, { intensity: 0.5 });
      break;
    }
    case 'lava': {
      // PL-07 — 55 Cancri e / CoRoT-7b: tidally locked, a molten day side and a cooler crust; rock vapour for air
      const v = pickOf([['basalt crust', 0x5a4a44], ['iron crust', 0x4a4448], ['ash crust', 0x62584e]] as const, u('v'));
      face = FACE.moon; variant = v[0]; tint = v[1]; rim = 0xff8a4a; atmosphere = 0.35; spin = 0;
      surface = { lava: { hot: 0xff6a14, cool: 0xa81804, gain: 1.25, scale: 2.6 + u('ls') * 1.2, width: 0.11, seas: 0.3 + u('lo') * 0.14, night: 0.3, seed } };
      shell = shellOf(0xff9a5a, { intensity: 0.6 });
      break;
    }
    case 'sub_neptune': {
      // PL-09 — GJ 1214b: aerosols mute everything, "a billiard ball" of haze; colour by chemistry
      // (the cyan-greens are the ice giants': a sub-Neptune's photochemical haze is greyer — steel, mauve or tholin tan)
      const v = pickOf([['steel-blue haze', 0xa9b9cc, 0xc0d4f0], ['mauve haze', 0xc2b6cc, 0xdccff0], ['pale tan haze', 0xd4c8b2, 0xf0e2c4]] as const, u('v'));
      face = FACE.gas; variant = v[0]; tint = 0xffffff; rim = v[2]; atmosphere = 1.3; spin = 0.005 + u('s') * 0.002;
      surface = { flatten: { color: v[1], amount: 0.88, keep: 0.5, mean: FACE_MEAN[FACE.gas].lum }, bands: { dark: 0xe6eaf0, light: 0xffffff, count: 7, contrast: 0.5, warp: 0.02, seed }, limb: { color: v[2], amount: 0.45, power: 2.6 } };
      shell = hazeOf(v[2], 1.04, 1.4, 0.75);
      break;
    }
    case 'gas_giant': {
      // PL-10 — Sudarsky class I–II: white zones over red-brown belts (Jupiter), pale butterscotch (Saturn), bright water cloud
      const v = pickOf([
        ['tan and rust', 0xf0e0cc, 0xb88462, 0xfff4e4, 0.55, 0.0, 0xffd9a0],
        ['pale butterscotch', 0xf2e4c0, 0xd8bc8a, 0xfff8e6, 0.35, 0.5, 0xffe6b0],
        ['white water cloud', 0xeef0f2, 0xc0c8d2, 0xffffff, 0.3, 0.62, 0xe6f0ff],
        ['umber and cream', 0xe8d2b8, 0x9a6a50, 0xfff0dc, 0.6, 0.15, 0xf6cfa0],
      ] as const, u('v'));
      face = FACE.gas; variant = v[0]; tint = v[1]; rim = v[6]; atmosphere = 1.4; spin = (0.007 + u('s') * 0.003) * spinDir;
      surface = {
        ...(v[5] > 0 ? { flatten: { color: v[1], amount: v[5], keep: 0.8, mean: FACE_MEAN[FACE.gas].lum } } : {}),
        bands: { dark: v[2], light: v[3], count: 9 + Math.floor(u('bc') * 7), contrast: v[4], warp: 0.035, seed },
        ...(u('st') < 0.7 ? { storm: { lat: (u('sl') - 0.5) * 0.9, lon: u('so') * 6.283, size: 0.09 + u('sz') * 0.08, color: u('sc') < 0.6 ? 0xc8603a : 0xf6ecdc, amount: 0.75 } } : {}),
      };
      shell = shellOf(v[6], { intensity: 0.85 });
      break;
    }
    case 'hot_giant': {
      // PL-11 — HD 189733b is a deep cobalt blue (silicate haze); TrES-2b reflects < 1 % and "emits a dim red glow … like a burning ember"
      const v = pickOf([['cobalt', 0x26447e, 0x6a9cf0, 0.22], ['coal', 0x2a2a30, 0x7a6a70, 0.4], ['umber', 0x4c3628, 0xc08a60, 0.3], ['slate blue', 0x34466a, 0x86a6dc, 0.25]] as const, u('v'));
      face = FACE.gas; variant = v[0]; tint = 0xffffff; rim = v[2]; atmosphere = 1.2; spin = 0;
      surface = { flatten: { color: v[1], amount: 0.84, keep: 0.6, mean: FACE_MEAN[FACE.gas].lum }, bands: { dark: 0xb0b4c0, light: 0xffffff, count: 5, contrast: 0.6, warp: 0.09, seed }, glow: { night: { color: 0xb0260c, gain: v[3] } } };
      shell = shellOf(v[2], { intensity: 0.9 });
      break;
    }
    case 'ultra_hot_giant': {
      // PL-12 — KELT-9b / WASP-121b: the day side glows like a cool star, the night side like an ember; clouds do not survive the day.
      // (4,600 K is a K star's orange-WHITE, 2,900 K an M dwarf's orange-red: the hottest variant is the palest — and past
      // 1.0 the sector's bloom is what makes it read as light, the display transform only pales it)
      const v = pickOf([['ember', 0x3c2018, 0xff6a28, 2.6], ['furnace', 0x40261a, 0xffa050, 3.4], ['dull iron', 0x342220, 0xf04a10, 2.1]] as const, u('v'));
      face = FACE.gas; variant = v[0]; tint = 0xffffff; rim = 0xffb070; atmosphere = 1.3; spin = 0;
      surface = { flatten: { color: v[1], amount: 0.88, keep: 0.5, mean: FACE_MEAN[FACE.gas].lum }, bands: { dark: 0xa8a0a0, light: 0xffffff, count: 4, contrast: 0.5, warp: 0.12, seed }, glow: { day: { color: v[2], gain: v[3], power: 1.25, shift: 0.3 }, night: { color: 0x8a1a08, gain: 0.3 } } };
      shell = hazeOf(0xffa860, 1.04, 1.2, 0.9);
      break;
    }
    case 'ice_giant': {
      // PL-13 — Uranus and Neptune are both a pale greenish-blue; Neptune only slightly bluer (Irwin et al. 2024)
      const v = pickOf([['pale greenish-blue', 0xa6d6da, 0xc4f2f0], ['pale azure', 0x96c4e0, 0xbfe0ff], ['sea-green', 0xa4dcd0, 0xc0f6e8]] as const, u('v'));
      face = FACE.gas; variant = v[0]; tint = 0xffffff; rim = v[2]; atmosphere = 1.3; spin = (0.006 + u('s') * 0.002) * spinDir;
      surface = {
        flatten: { color: v[1], amount: 0.93, keep: 0.4, mean: FACE_MEAN[FACE.gas].lum },
        bands: { dark: 0xe2ecf0, light: 0xffffff, count: 6, contrast: 0.55, warp: 0.015, seed },
        ...(u('st') < 0.4 ? { storm: { lat: (u('sl') - 0.5) * 0.8, lon: u('so') * 6.283, size: 0.07 + u('sz') * 0.05, color: 0x4a6a8a, amount: 0.5 } } : {}),
        limb: { color: v[2], amount: 0.25, power: 3 },
      };
      shell = shellOf(v[2], { intensity: 1.0 });
      break;
    }
    case 'super_puff': {
      // PL-14 — Kepler-51: Saturn-sized, a few Earth masses; the thickest haze found on a planet — pale, low contrast, a silhouette with no edge
      const v = pickOf([['pale rose haze', 0xe8d8d2, 0xf6e4dc], ['pale lavender haze', 0xdad8ea, 0xe8e6ff], ['pale straw haze', 0xe8e2c8, 0xf6f0d8]] as const, u('v'));
      face = FACE.gas; variant = v[0]; tint = 0xffffff; rim = v[2]; atmosphere = 1.4; spin = 0.003 + u('s') * 0.002;
      surface = { flatten: { color: v[1], amount: 0.96, keep: 0.25, mean: FACE_MEAN[FACE.gas].lum }, limb: { color: v[2], amount: 0.6, power: 1.8 } };
      shell = hazeOf(v[2], 1.1, 2.2, 0.75);
      break;
    }
  }
  // NE-21: aurorae on a giant with a magnetosphere — ovals round a tilted dipole, brightest on the night side
  if (p.aurora && (p.kind === 'gas_giant' || p.kind === 'ice_giant')) {
    surface = { ...(surface ?? {}), aurora: { color: 0xff4a9a, color2: 0x7a5aff, gain: 2.6, colat: 0.26 + u('ac') * 0.08, width: 0.035, tilt: 0.12 + u('at') * 0.14, lon: u('al') * 6.283, seed } };
  }
  const row = opt.faces === false ? null : planetFaceAt(p.kind, u('face'));
  surface = wearFace(surface, row);
  return { kind: p.kind, variant, face, faceKey: row ? row.name : null, tint, rim, atmosphere, ocean, surface, shell, spin, mean: meanOf(face, tint, surface, row?.rgb) };
}
export const PLANET_KINDS: ReadonlyArray<PlanetKind> = ['airless', 'desert', 'temperate', 'water', 'greenhouse', 'frozen', 'lava', 'sub_neptune', 'gas_giant', 'hot_giant', 'ultra_hot_giant', 'ice_giant', 'super_puff'];

/**
 * How two kinds differ to the eye, 0..∞ (≥ 0.12 reads as a different place): the day side's mean colour, how much air
 * wraps the terminator, whether it glows, whether it wears a shell and how far that reaches.
 */
export function kindDistance(a: PlanetKindSpec, b: PlanetKindSpec): number {
  const dc = Math.hypot(a.mean[0] - b.mean[0], a.mean[1] - b.mean[1], a.mean[2] - b.mean[2]);
  const glow = (s: PlanetKindSpec): number => (s.surface?.lava ? 1 : 0) + (s.surface?.glow?.day ? 1 : 0) + (s.surface?.glow?.night ? 0.5 : 0);
  const shell = (s: PlanetKindSpec): number => (s.shell ? (s.shell.extra.outer ?? 1.025) - 1 + 0.05 : 0);
  return dc + Math.abs(a.atmosphere - b.atmosphere) * 0.12 + Math.abs(glow(a) - glow(b)) * 0.4 + Math.abs(shell(a) - shell(b)) * 1.5
    + (a.ocean !== b.ocean ? 0.1 : 0) + (a.face !== b.face ? 0.06 : 0) + (!!a.surface?.bands !== !!b.surface?.bands ? 0.05 : 0)
    + (!!a.surface?.drown !== !!b.surface?.drown ? 0.08 : 0); // a world with no coasts
}

// ── the flags' own pieces ───────────────────────────────────────────────────────────────────────────────────────
/** PL-15 — an escaping envelope: a faint tail streaming AWAY from the star (lengths in planet radii). */
export interface EvapTailSpec { color: number; lengthR: number; widthR: number; gain: number; dust: boolean }
export function evapTailSpec(kind: PlanetKind, seed: number): EvapTailSpec {
  const u = mix01(`evap:${seed}`);
  // a rocky world sheds DUST (KIC 12557548 b: a comet-like tail of rock grains); a giant sheds hydrogen (GJ 436b's cloud
  // is far bigger than its star, but only in ultraviolet — to the eye a faint pale-blue wisp)
  return kind === 'lava'
    ? { color: 0xd8c0a0, lengthR: 7 + u * 4, widthR: 1.1, gain: 0.3, dust: true }
    : { color: 0x9cc4ff, lengthR: 9 + u * 6, widthR: 1.6, gain: 0.34, dust: false };
}
/** NE-09 — a forming giant's own disc (planet radii): dusty, warm at its inner edge, ending well inside the Hill sphere. */
export interface PlanetDiscSpec { rIn: number; rOut: number; tau: number; color: number; warm: number; tilt: number; node: number }
export function circumplanetarySpec(seed: number): PlanetDiscSpec {
  const u = (k: string): number => mix01(`cpd:${seed}:${k}`);
  return { rIn: 1.25 + u('i') * 0.15, rOut: 2.3 + u('o') * 0.5, tau: 1.6, color: 0xb89070, warm: 0xff8a3a, tilt: 0.1 + u('t') * 0.25, node: u('n') * 6.283 };
}
/** NE-21 — the plasma torus a volcanic moon feeds: a faint ring of sulphur and sodium light at the moon's orbit (planet radii). */
export interface TorusSpec { r: number; width: number; color: number; gain: number }
export function plasmaTorusSpec(moonOrbitR: number): TorusSpec {
  // "much of it is invisible … a luminous transparent force-field shell is misleading": ionised sulphur's red and
  // sodium's yellow, seen from the ground only through a coronagraph — the gain keeps it at the edge of sight
  return { r: moonOrbitR, width: Math.max(0.12, moonOrbitR * 0.07), color: 0xffb060, gain: 0.022 };
}

// ── moons ───────────────────────────────────────────────────────────────────────────────────────────────────────
export interface MoonKindSpec {
  kind: MoonKind;
  /** The POOLED face (the fallback) and the catalog face worn instead when it loads (null = the pooled one). */
  face: number;
  faceKey: string | null;
  tint: number;
  rim: number;
  atmosphere: number;
  surface: PlanetSurface | null;
  shell: { hue: number; extra: ShellExtra } | null;
  /** A lumpy body: the radial relief (fraction of its radius; 0 = a sphere) and its long-axis stretch. */
  lumps: number;
  stretch: number;
  /** Plumes: how many, where (a pole or anywhere), how tall (moon radii), their colour and gain. */
  plumes: { count: number; polar: boolean; heightR: number; color: number; gain: number } | null;
  /** rad/s (moons are tidally locked: one turn per orbit is the caller's; this adds nothing — kept for an irregular's tumble). */
  tumble: number;
  mean: [number, number, number];
}
export const MOON_KINDS: ReadonlyArray<MoonKind> = ['rocky', 'volcanic', 'icy_ocean', 'plume', 'hazy', 'irregular'];
export function moonKindSpec(m: Pick<MoonMakeup, 'kind' | 'seed' | 'shepherd'>, opt: KindOpts = {}): MoonKindSpec {
  const u = (k: string): number => mix01(`mk:${m.seed}:${k}`);
  const seed = m.seed % 997;
  const base = { atmosphere: 0, surface: null as PlanetSurface | null, shell: null as MoonKindSpec['shell'], lumps: 0, stretch: 1, plumes: null as MoonKindSpec['plumes'], tumble: 0 };
  let s: Omit<MoonKindSpec, 'mean' | 'faceKey'>;
  switch (m.kind) {
    case 'rocky': // PL-18 — craters, maria, regolith
      s = { ...base, kind: m.kind, face: FACE.moon, tint: pickOf([0xc2beb8, 0xb4aca4, 0xcac4b6], u('t')), rim: 0x8a8680 };
      break;
    case 'volcanic': // PL-19 — Io: sulphur yellows and oranges, dark calderas, red rings; "the entire moon need not glow"
      s = {
        ...base, kind: m.kind, face: FACE.moon, tint: 0xffffff, rim: 0xd8c070,
        surface: {
          flatten: { color: 0xe6cf6e, amount: 0.8, keep: 1.25, mean: FACE_MEAN[FACE.moon].lum },
          blotch: { color: 0xd4622a, amount: 0.6, scale: 2.6, seed },
          lava: { hot: 0xff7a20, cool: 0xc02808, gain: 1.5, scale: 4.2, width: 0, seas: 0, night: 1, seed, spots: 0.55 },   // hot spots only: "13 hot spots … each on a caldera floor" (Galileo, in eclipse)
        },
        plumes: { count: 1 + (u('p') < 0.5 ? 1 : 0), polar: false, heightR: 0.2, color: 0xb8ccff, gain: 0.32 },   // Pele's reached 400 km over a 1,821 km moon; "plumes on Io have a blue color"
      };
      break;
    case 'icy_ocean': // PL-20 — Europa: bright water ice crossed by red-brown lineae
      s = {
        ...base, kind: m.kind, face: FACE.ice, tint: 0xffffff, rim: 0xd8e8f0,
        surface: {
          flatten: { color: 0xf0ece2, amount: 0.78, keep: 0.5, mean: FACE_MEAN[FACE.ice].lum },
          blotch: { color: 0xc09a78, amount: 0.35, scale: 1.8, seed },
          lines: { color: 0x8a5438, amount: 0.85, scale: 3.4, width: 0.075, seed, polar: -2 },
          gloss: { strength: 0.3, roughness: 0.35 },
        },
      };
      break;
    case 'plume': // PL-21 — Enceladus: the brightest surface there is, blue-grey "tiger stripes" at one pole, jets seen best back-lit
      s = {
        ...base, kind: m.kind, face: FACE.ice, tint: 0xffffff, rim: 0xe8f4ff,
        surface: {
          flatten: { color: 0xf8fbff, amount: 0.9, keep: 0.35, mean: FACE_MEAN[FACE.ice].lum },
          lines: { color: 0x6a9ab8, amount: 0.8, scale: 5, width: 0.07, seed, polar: -0.55 },
          gloss: { strength: 0.35, roughness: 0.3 },
        },
        plumes: { count: 3, polar: true, heightR: 1.4, color: 0xdfeeff, gain: 0.55 },
      };
      break;
    case 'hazy': // PL-22 — Titan: an orange haze that hides the ground, a soft limb, an atmosphere a tenth of the radius deep
      s = {
        ...base, kind: m.kind, face: FACE.gas, tint: 0xffffff, rim: 0xe8a860, atmosphere: 1.4,
        surface: { flatten: { color: 0xd89a4a, amount: 0.97, keep: 0.2, mean: FACE_MEAN[FACE.gas].lum }, limb: { color: 0xe8b070, amount: 0.5, power: 2.2 } },
        shell: hazeOf(0xe89a50, 1.07, 1.8, 0.7, 0x8aa8e8),   // ("a hazy orange globe surrounded by a tenuous, bluish haze" — Cassini, PIA06090)
      };
      break;
    default: // PL-23 — a captured body: small, dark, lumpy; a shepherd wears its ring's pale dust
      s = m.shepherd
        ? { ...base, kind: 'irregular', face: FACE.moon, tint: 0xc8c0b0, rim: 0x8a8680, lumps: 0.16, stretch: 1.45 }
        : { ...base, kind: 'irregular', face: FACE.moon, tint: pickOf([0x7a7068, 0x6e6a70, 0x847464], u('t')), rim: 0x8a8680, lumps: 0.24, stretch: 1.2 + u('st') * 0.3, tumble: 0.01 + u('tu') * 0.02 };
  }
  const row = opt.faces === false ? null : moonFaceAt(m.kind, m.kind === 'irregular' && !!m.shepherd, u('face'));
  const surface = wearFace(s.surface, row);
  return { ...s, surface, faceKey: row ? row.name : null, mean: meanOf(s.face, s.tint, surface, row?.rgb) };
}

// ── the small worlds of the outer dark (PL-16, PL-17, SY-06) ────────────────────────────────────────────────────
/** What a small world wears (planetMaterial's inputs). */
export interface SmallWorldSpec { variant: string; face: number; tint: number; rim: number; atmosphere: number; surface: PlanetSurface | null }
/**
 * PL-17 — a dwarf planet: a small globe of ices, not an asteroid. Reddish-brown where sunlight has cooked methane into
 * tholins, with one broad pale basin of fresh ice (Pluto's "heart"); or bright methane frost (Eris, Makemake); or nearly
 * pure water ice with one dark red spot (Haumea).
 */
export function dwarfPlanetSpec(seed: number): SmallWorldSpec {
  const u = (k: string): number => mix01(`dwarf:${seed}:${k}`);
  const s = seed % 997;
  const heart = { lat: (u('hl') - 0.3) * 0.6, lon: u('ho') * 6.283, size: 0.26 + u('hs') * 0.1 };
  const v = Math.floor(u('v') * 3);
  if (v === 0) return { variant: 'tholin and frost', face: FACE.ice, tint: 0xffffff, rim: 0xb8c8e0, atmosphere: 0.15,
    surface: { flatten: { color: 0xd2b094, amount: 0.72, keep: 0.9, mean: FACE_MEAN[FACE.ice].lum }, blotch: { color: 0x6a3c2e, amount: 0.75, scale: 1.5, seed: s }, storm: { ...heart, color: 0xf4ece0, amount: 0.9 } } };
  if (v === 1) return { variant: 'methane frost', face: FACE.ice, tint: 0xffffff, rim: 0xd8e4f0, atmosphere: 0.1,
    surface: { flatten: { color: 0xf0e8e0, amount: 0.8, keep: 0.6, mean: FACE_MEAN[FACE.ice].lum }, blotch: { color: 0xb08870, amount: 0.4, scale: 2.1, seed: s }, gloss: { strength: 0.2, roughness: 0.4 } } };
  return { variant: 'water ice, one dark spot', face: FACE.ice, tint: 0xffffff, rim: 0xd8e4f0, atmosphere: 0,
    surface: { flatten: { color: 0xe6ecf0, amount: 0.75, keep: 0.7, mean: FACE_MEAN[FACE.ice].lum }, storm: { ...heart, size: 0.16, color: 0x8a4a3a, amount: 0.8 }, gloss: { strength: 0.25, roughness: 0.38 } } };
}
/** SY-06 — the dwarf planet's large moon: grey water ice with a dark red cap at one pole (its planet's escaped methane, frozen there and cooked — Charon). */
export function dwarfMoonSpec(seed: number): MoonKindSpec {
  const north = mix01(`dwarfmoon:${seed}:n`) < 0.5;
  const s: Omit<MoonKindSpec, 'mean'> = {
    kind: 'rocky', face: FACE.moon, faceKey: null, tint: 0xbcb8b4, rim: 0x8a8680, atmosphere: 0, shell: null, lumps: 0, stretch: 1, plumes: null, tumble: 0,
    surface: { cap: { lat: 0.7, color: 0x7a463a, soft: 0.12, north, south: !north } },
  };
  return { ...s, mean: meanOf(s.face, s.tint, s.surface) };
}
/**
 * PL-16 — a world with no star. It reflects almost nothing (the system's star is a point 2,000 km away: `sun` 0.04), so it
 * is a dark disc that hides the stars behind it; a young one still breathes out its formation heat (a deep red that
 * the belts carry unevenly), and its own magnetic field lights aurorae with no sun to drive them (SIMP J0136).
 */
export function rogueSpec(seed: number): SmallWorldSpec {
  const u = (k: string): number => mix01(`rogue:${seed}:${k}`);
  const s = seed % 997;
  const young = u('y') < 0.6;
  return {
    variant: young ? 'young, still warm' : 'old and cold', face: FACE.gas, tint: 0xffffff, rim: 0x3a3048, atmosphere: 0.8,
    surface: {
      flatten: { color: young ? 0x4a3038 : 0x2e3440, amount: 0.85, keep: 0.7, mean: FACE_MEAN[FACE.gas].lum },
      bands: { dark: 0x9a94a0, light: 0xffffff, count: 8, contrast: 0.5, warp: 0.05, seed: s },
      glow: { all: { color: young ? 0x8a1c2c : 0x30182a, gain: young ? 0.06 : 0.012 } },
      aurora: { color: 0xff4a9a, color2: 0x7a5aff, gain: young ? 1.8 : 1.1, colat: 0.3 + u('ac') * 0.08, width: 0.04, tilt: 0.15 + u('at') * 0.2, lon: u('al') * 6.283, seed: s },
      sun: 0.04,
    },
  };
}
/** SY-08 — trojans are D and P types: dark, reddish, matte (albedo 0.03–0.07). */
export const TROJAN_LOOK: TaxonomyLook = { color: 0x55443a, albedo: 0.05, roughness: 0.96, metalness: 0, emissive: 0x000000 };
/** SB-12 — an outer-belt body's colour from neutral grey to muted red (irradiated organics): `red` 0..1. */
export function tnoTint(red: number): number {
  const t = clamp(fin(red, 0), 0, 1), a = hexRgb(0xb4b2ae), b = hexRgb(0x9a5c46);
  const c = (i: number): number => Math.round((a[i] + (b[i] - a[i]) * t) * 255);
  return (c(0) << 16) | (c(1) << 8) | c(2);
}
/** A comet's colours and gains. The nucleus is "blacker than coal" (albedo ~0.04); the ion tail fluoresces blue (CO⁺ near 420 nm); the dust is sunlit grey-yellow. */
export const COMET_LOOK = {
  nucleus: { color: 0x34302e, albedo: 0.04, roughness: 1, metalness: 0, emissive: 0x000000 } as TaxonomyLook,
  coma: 0xdfeef0, comaFar: 0x9fd8c8, comaGain: 0.5,
  ion: 0x7fb8ff, ionFar: 0x4f7fe8, ionGain: 0.5,
  dust: 0xfff0d6, dustFar: 0xe8d8b8, dustGain: 0.6,
  jetGain: 0.5,
} as const;
/** SB-11 — centaurs come in two colours and little between: neutral grey-blue (Chiron) or very red (Pholus). */
export function centaurLook(seed: number): TaxonomyLook {
  return mix01(`centaur:${seed}`) < 0.5
    ? { color: 0x74767c, albedo: 0.08, roughness: 0.95, metalness: 0, emissive: 0x000000 }
    : { color: 0x8a4a34, albedo: 0.1, roughness: 0.95, metalness: 0, emissive: 0x000000 };
}
/** SB-13 — the first interstellar visitor seen was reddish, inert and ~6–10 times longer than wide, tumbling every few hours. `tumble` rad/s (game clock). */
export const VISITOR_LOOK = { color: 0x7a4638, albedo: 0.1, roughness: 0.92, metalness: 0.05, emissive: 0x000000, tumble: 0.035 } as const;

// ── THE MOON'S SEAT (the far-body trick applied to a moon against its own planet) ───────────────────────────────
/** SpaceModule's far-body anchor distance (m) — pinned against its source by the harness. */
export const FAR_ANCHOR_M = 100_000;
/** The sector camera's near plane (m). */
export const SECTOR_NEAR_M = 0.05;
/** Depth codes a drawn moon is kept from its planet's cap (the BODY LAYER LAW's margins: `over` 8, a near `behind` 4). */
export const MOON_SEAT_CODES = { front: 8, behind: 4, farSafe: 4 } as const;
/** How many 24-bit depth codes in front of the far plane a point at view distance `z` (m) lies. */
export const depthCodes = (z: number, near = SECTOR_NEAR_M): number => near / (Math.max(z, 1e-6) * DEPTH_CODE);
/** The view distance (m) that lies `codes` in front of the far plane. */
export const depthDistance = (codes: number, near = SECTOR_NEAR_M): number => near / (Math.max(codes, 1e-9) * DEPTH_CODE);
/** A body's drawn distance from the ship under THE FAR-BODY LAW. */
export const anchoredDistance = (d: number): number => (d > FAR_ANCHOR_M ? FAR_ANCHOR_M : d);
/** On the camera's side of the planet's LIMB plane (P·C > R² — rings.ringHalf): nothing there is behind the visible cap. */
export function moonInFront(moonRel: V3, camRel: V3, R: number): boolean {
  return moonRel[0] * camRel[0] + moonRel[1] * camRel[1] + moonRel[2] * camRel[2] > R * R;
}
/**
 * WHERE A MOON IS DRAWN ALONG ITS OWN VIEW RAY. A moon of a far planet stands at the same 100 km as its planet, where
 * one depth code is ~12 km: the buffer cannot order the two. So the moon is moved along its view ray (and scaled with
 * it — the same angle, the same picture) until the buffer can: IN FRONT of the planet's limb plane it is drawn at
 * least `front` codes nearer than the planet's nearest point; BEHIND it, at least `behind` codes beyond the limb and
 * never within `farSafe` codes of the far plane (THE BODY'S SEAT: an opaque there is painted over by the sky).
 * A moon the buffer already resolves keeps its distance. Returns the factor on its drawn distance (1 = untouched).
 *
 * `planetD` the planet centre's DRAWN distance from the camera, `planetRk` its drawn radius, `moonD` the moon centre's
 * drawn distance, `moonRk` its drawn radius — all in metres as drawn (after the far-body anchor).
 */
export function moonSeatFactor(front: boolean, planetD: number, planetRk: number, moonD: number, moonRk: number, near = SECTOR_NEAR_M): number {
  if (!(planetD > 0 && moonD > 0) || !Number.isFinite(planetD + planetRk + moonD + moonRk)) return 1;
  const capNear = Math.max(planetD - planetRk, near * 4);
  const limb = Math.sqrt(Math.max(planetD * planetD - planetRk * planetRk, capNear * capNear));
  if (front) {
    // the moon's FAR side must clear the cap's nearest point by the margin
    const want = depthDistance(depthCodes(capNear, near) + MOON_SEAT_CODES.front, near);
    const far = moonD + moonRk;
    return far <= want ? 1 : clamp(want / far, 1e-3, 1);
  }
  // the moon's NEAR side must lie beyond the limb by the margin, yet short of the far plane's last codes
  const codes = Math.max(MOON_SEAT_CODES.farSafe, depthCodes(limb, near) - MOON_SEAT_CODES.behind);
  const want = depthDistance(codes, near);
  const nearSide = Math.max(moonD - moonRk, near);
  return nearSide >= want ? 1 : clamp(want / nearSide, 1, 1e3);
}

// ── comets (SB-08 … SB-10) ──────────────────────────────────────────────────────────────────────────────────────
const dot3 = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const len3 = (a: V3): number => Math.hypot(a[0], a[1], a[2]);
const norm3 = (a: V3, out: V3 = [0, 0, 0]): V3 => { const l = len3(a) || 1; out[0] = a[0] / l; out[1] = a[1] / l; out[2] = a[2] / l; return out; };
/** The starlight (× a G dwarf at 600 km — system_makeup.HEAT_REF_M) at which ice begins to sublime, and at which a comet is fully awake. */
export const COMET_WAKE = { on: 0.1, full: 2.2 } as const;
export const COMET_VARIANT_GAIN: Readonly<Record<'active' | 'main_belt' | 'dormant', number>> = { active: 1, main_belt: 0.22, dormant: 0 };
/**
 * A comet's ACTIVITY 0..1 at `distM` from a star of `starHeat` (system_makeup.STAR_HEAT): nothing beyond the frost line,
 * growing with the logarithm of the starlight to full. Monotone in distance. `distM` = Infinity (the Commons Reach,
 * whose sun is a direction) is fully awake.
 */
export function cometActivity(distM: number, starHeat: number, variant: 'active' | 'main_belt' | 'dormant' = 'active'): number {
  const gain = COMET_VARIANT_GAIN[variant];
  if (gain <= 0) return 0;
  if (distM === Infinity) return gain;
  if (!(distM > 0) || !(starHeat > 0)) return 0;
  const s = starHeat * (600_000 / distM) ** 2;
  return gain * clamp(Math.log(s / COMET_WAKE.on) / Math.log(COMET_WAKE.full / COMET_WAKE.on), 0, 1);
}
/** A comet's sizes at an activity (m): the coma, the ion tail and the dust tail. Game scale — a tail is a few planet-orbits long, never a wall. */
export function cometSizes(activity: number, nucleusM: number): { comaM: number; ionM: number; dustM: number; ionWidthM: number; dustWidthM: number } {
  const a = clamp(fin(activity, 0), 0, 1), n = clamp(fin(nucleusM, 800), 200, 3000) / 1000;
  const comaM = a <= 0 ? 0 : (9_000 + 26_000 * a) * (0.8 + 0.2 * n);
  return { comaM, ionM: 620_000 * a ** 1.3, dustM: 330_000 * a ** 1.15, ionWidthM: comaM * 0.55 + 6_000 * a, dustWidthM: comaM * 1.2 + 70_000 * a };
}
/** How far the dust tail bends back along the orbit, as a share of its length (the fan's leading and trailing edges). */
export const DUST_CURL = { lead: 0.05, trail: 0.62 } as const;
export interface CometFrame {
  /** Unit, AWAY from the star: the ion tail's axis. */
  anti: V3;
  /** Unit, perpendicular to `anti`, opposite the comet's motion in its orbit plane: the way the dust lags. Zero when it moves straight along the star's line. */
  lag: V3;
  /** 0..1 — how much of the motion is across the star's line (the dust tail's curl scales with it). */
  cross: number;
}
/** The tail frame from the comet's place and velocity about the star at the origin. THE TAILS POINT AWAY FROM THE STAR, never behind the motion. */
export function cometFrame(pos: V3, vel: V3): CometFrame {
  const anti = norm3(pos);
  const sp = len3(vel);
  if (!(sp > 1e-9)) return { anti, lag: [0, 0, 0], cross: 0 };
  const along = dot3(vel, anti);
  const lx = -(vel[0] - anti[0] * along), ly = -(vel[1] - anti[1] * along), lz = -(vel[2] - anti[2] * along);
  const ll = Math.hypot(lx, ly, lz);
  if (ll < 1e-9 * sp) return { anti, lag: [0, 0, 0], cross: 0 };
  return { anti, lag: [lx / ll, ly / ll, lz / ll], cross: clamp(ll / sp, 0, 1) };
}
/** A comet standing still under a sun given as a direction (the Commons Reach): the lag is a seeded perpendicular. */
export function cometFrameFixed(sunDir: V3, seed: number): CometFrame {
  const anti = norm3([-sunDir[0], -sunDir[1], -sunDir[2]]);
  const a = mix01(`cometlag:${seed}`) * Math.PI * 2;
  const ref: V3 = Math.abs(anti[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const u = norm3([anti[1] * ref[2] - anti[2] * ref[1], anti[2] * ref[0] - anti[0] * ref[2], anti[0] * ref[1] - anti[1] * ref[0]]);
  const v: V3 = [anti[1] * u[2] - anti[2] * u[1], anti[2] * u[0] - anti[0] * u[2], anti[0] * u[1] - anti[1] * u[0]];
  return { anti, lag: norm3([u[0] * Math.cos(a) + v[0] * Math.sin(a), u[1] * Math.cos(a) + v[1] * Math.sin(a), u[2] * Math.cos(a) + v[2] * Math.sin(a)]), cross: 0.8 };
}
/** A point of the ION tail: `s` 0..1 along it, straight down the anti-solar line. */
export function ionTailPoint(f: CometFrame, lengthM: number, s: number, out: V3 = [0, 0, 0]): V3 {
  out[0] = f.anti[0] * lengthM * s; out[1] = f.anti[1] * lengthM * s; out[2] = f.anti[2] * lengthM * s;
  return out;
}
/**
 * A point of the DUST tail's fan, relative to the nucleus: `s` 0..1 along it, `w` 0..1 across the fan (0 = its leading
 * edge, nearest the anti-solar line; 1 = its trailing edge). Dust leaves slowly and is pushed off by light, so each
 * grain falls behind the comet on its own orbit: the tail starts anti-solar and curls back the way the comet came.
 */
export function dustTailPoint(f: CometFrame, lengthM: number, s: number, w: number, out: V3 = [0, 0, 0]): V3 {
  const curl = (DUST_CURL.lead + (DUST_CURL.trail - DUST_CURL.lead) * w) * f.cross;
  const a = lengthM * s * (1 - 0.25 * curl * s), l = lengthM * curl * s * s;
  out[0] = f.anti[0] * a + f.lag[0] * l; out[1] = f.anti[1] * a + f.lag[1] * l; out[2] = f.anti[2] * a + f.lag[2] * l;
  return out;
}
/** A body's velocity on its orbit (m/s), by a centred difference on the wall clock. */
export function orbitVelocity(o: OrbitMakeup, tS: number, out: V3 = [0, 0, 0]): V3 {
  const h = Math.max(0.5, o.periodS * 1e-4);
  const a = orbitAt(o, tS - h), b = orbitAt(o, tS + h);
  out[0] = (b[0] - a[0]) / (2 * h); out[1] = (b[1] - a[1]) / (2 * h); out[2] = (b[2] - a[2]) / (2 * h);
  return out;
}
/** Where an interstellar visitor is on its one straight pass (SB-13). */
export function visitorAt(f: { from: V3; to: V3; periodS: number; phase0: number }, tS: number, out: V3 = [0, 0, 0]): V3 {
  const x = tS / f.periodS + f.phase0, u = x - Math.floor(x);
  out[0] = f.from[0] + (f.to[0] - f.from[0]) * u; out[1] = f.from[1] + (f.to[1] - f.from[1]) * u; out[2] = f.from[2] + (f.to[2] - f.from[2]) * u;
  return out;
}

// ── small bodies (SB-01 … SB-07) ────────────────────────────────────────────────────────────────────────────────
export interface TaxonomyLook { color: number; albedo: number; roughness: number; metalness: number; emissive: number }
/**
 * What a belt is made of. Carbonaceous bodies are charcoal (albedo ~0.05), stony ones grey-tan and several times
 * brighter (~0.2), metal-rich ones a weathered grey with a sheen — never chrome — and icy ones pale but not gleaming.
 * `color` multiplies the rock's own texture; `albedo` is the brightness the harness orders.
 */
export const TAXONOMY: Readonly<Record<FieldTaxonomy, TaxonomyLook>> = {
  carbonaceous: { color: 0x3e3b39, albedo: 0.05, roughness: 0.97, metalness: 0.0, emissive: 0x000000 },
  stony: { color: 0xb8a890, albedo: 0.2, roughness: 0.9, metalness: 0.05, emissive: 0x000000 },
  metallic: { color: 0x9a9690, albedo: 0.14, roughness: 0.5, metalness: 0.75, emissive: 0x000000 },
  icy: { color: 0xd4dce2, albedo: 0.4, roughness: 0.42, metalness: 0.02, emissive: 0x000000 },
};
/** A rock's shape by what holds it together: a rubble pile slumps toward round, a coherent fragment keeps its corners. */
export function rockShape(structure: 'rubble' | 'coherent', key: string): { sx: number; sy: number; sz: number; flat: boolean } {
  const u = (k: string): number => mix01(`${key}:${k}`);
  return structure === 'rubble'
    ? { sx: 0.92 + u('x') * 0.16, sy: 0.78 + u('y') * 0.2, sz: 0.92 + u('z') * 0.16, flat: false }
    : { sx: 0.62 + u('x') * 0.3, sy: 0.55 + u('y') * 0.5, sz: 1.05 + u('z') * 0.55, flat: true };
}
export const LANDMARK_KINDS: ReadonlyArray<FieldLandmark> = ['contact_binary', 'binary_asteroid', 'active_asteroid', 'rubble_pile'];
/** A belt's one named body: its parts (centres and radii in units of `radiusM`), whether they orbit each other, and its tail. */
export function landmarkParts(kind: FieldLandmark, seed: number): { lobes: Array<{ at: V3; r: number; squash: number }>; orbitS: number; tail: boolean; top: boolean } {
  const u = (k: string): number => mix01(`lm:${seed}:${k}`);
  switch (kind) {
    case 'contact_binary': { // SB-06: two lobes joined by a neck (Arrokoth, 67P) — touching, never apart
      const r1 = 0.62 + u('a') * 0.08, r2 = 0.42 + u('b') * 0.1;
      return { lobes: [{ at: [-(r2 * 0.92), 0, 0], r: r1, squash: 0.82 }, { at: [r1 * 0.92, 0.04, 0], r: r2, squash: 0.9 }], orbitS: 0, tail: false, top: false };
    }
    case 'binary_asteroid': { // SY-07: a primary and a small companion on a real orbit (Didymos–Dimorphos)
      const r2 = 0.24 + u('b') * 0.1;
      return { lobes: [{ at: [0, 0, 0], r: 0.72, squash: 0.88 }, { at: [2.3 + u('d') * 0.6, 0, 0], r: r2, squash: 0.9 }], orbitS: 240 + u('p') * 180, tail: false, top: false };
    }
    case 'active_asteroid': // SB-07: a rock with a faint dust tail — activity, not proof of ice
      return { lobes: [{ at: [0, 0, 0], r: 0.8, squash: 0.85 }], orbitS: 0, tail: true, top: false };
    default: // SB-04: a top-shaped rubble pile with an equatorial ridge, strewn with boulders (Bennu, Ryugu)
      return { lobes: [{ at: [0, 0, 0], r: 0.85, squash: 0.92 }], orbitS: 0, tail: false, top: true };
  }
}
/** A top-shaped body's radius at sin-latitude `y` (−1..1), in units of its equatorial radius: two cones meeting at a ridge. */
export function topShapeRadius(y: number): number {
  const a = Math.abs(clamp(y, -1, 1));
  return (1 - 0.42 * a) * (1 + 0.07 * Math.exp(-(a * a) / 0.006));
}
/** SY-08 — a trojan swarm's bodies: spread through its sphere, denser toward the middle, never a pile. */
export function swarmBodies(key: string, count: number, radiusM: number): Array<{ at: V3; sizeM: number }> {
  const out: Array<{ at: V3; sizeM: number }> = [];
  for (let i = 0; i < count; i++) {
    const u = (k: string): number => mix01(`${key}:${i}:${k}`);
    const th = u('a') * Math.PI * 2, cz = u('b') * 2 - 1, sz = Math.sqrt(1 - cz * cz), r = radiusM * Math.pow(u('r'), 0.6);
    out.push({ at: [Math.cos(th) * sz * r, cz * r * 0.55, Math.sin(th) * sz * r], sizeM: 140 + u('s') ** 2 * 620 });
  }
  return out;
}
/** SY-10 — the outer icy belt: bodies spread round the whole ring, hundreds of km apart; grey to muted red (SB-12). */
export function tnoBodies(key: string, count: number, radiusM: number, widthM: number): Array<{ at: V3; sizeM: number; red: number }> {
  const out: Array<{ at: V3; sizeM: number; red: number }> = [];
  for (let i = 0; i < count; i++) {
    const u = (k: string): number => mix01(`${key}:${i}:${k}`);
    const a = ((i + u('a') * 0.8) / count) * Math.PI * 2;
    const r = radiusM + (u('r') + u('r2') - 1) * widthM * 0.5;
    out.push({ at: [Math.cos(a) * r, (u('y') - 0.5) * widthM * 0.35, Math.sin(a) * r], sizeM: 380 + u('s') ** 2.2 * 2100, red: u('c') });
  }
  return out;
}
/** SB-14 — the pebbles a comet shed: mean-anomaly offsets BEHIND it on its own orbit (rad, small), a sideways drift (m) and a size (m). */
export function streamPebbles(key: string, count: number): Array<{ dM: number; side: V3; sizeM: number }> {
  const out: Array<{ dM: number; side: V3; sizeM: number }> = [];
  for (let i = 0; i < count; i++) {
    const u = (k: string): number => mix01(`${key}:${i}:${k}`);
    // most trail the comet, a few lead it (grains leave both ways); the stream thins away from the nucleus
    const behind = u('f') < 0.82 ? 1 : -1;
    out.push({ dM: -behind * (0.004 + u('m') ** 1.8 * 0.2), side: [(u('x') - 0.5) * 5_000, (u('y') - 0.5) * 3_000, (u('z') - 0.5) * 5_000], sizeM: 5 + u('s') ** 2 * 34 });
  }
  return out;
}

// ── system-plane discs (SB-15, NE-10, NE-08) ────────────────────────────────────────────────────────────────────
export type DiscKind = 'dust_band' | 'debris_disc' | 'proto_disc';
export interface DiscSpec { kind: DiscKind; innerM: number; outerM: number; gapsM: ReadonlyArray<number>; strength: number; seed: number }
export interface DiscLook {
  /** Dust albedo colour (sRGB hex) and the Henyey–Greenstein asymmetry (forward scattering > 0). */
  color: number; g: number;
  /** Scattered-light gain, and the thermal glow of the hot inner edge (colour, gain, the radius share it reaches). */
  scatter: number; warm: number; warmGain: number; warmReach: number;
  /** Peak vertical optical depth through the midplane (≪ 1 = a faint band that dims nothing). */
  tau: number;
  /** Scale height h/r at the inner edge and its flaring exponent (h/r ∝ r^flare). */
  hr: number; flare: number;
  /** How far past `outerM` and inside `innerM` the density tapers (share of the width). */
  taper: number;
  /** Each gap's half-width as a share of its radius, and how empty it is. */
  gapWidth: number; gapDepth: number;
  /**
   * A forming disc's RING SHEET: the settled grains of its midplane, far thinner than the gas (the layer ALMA sees — HL
   * Tau's rings). `sheet` is its vertical optical depth at full density (0 = none), `ember` the gain of its own glow.
   */
  ember: number; sheet: number;
  /** The glow of the disc's lit SURFACE layer (the gain; see DISC_HAZE_DEPTH). */
  haze: number;
  /** The share of what a THICK disc stops that it scatters on (single-scattering albedo). */
  albedo: number;
}
/**
 * The three discs. A zodiacal BAND is an optically thin, strongly forward-scattering haze of comet and asteroid dust
 * (brightest toward the star); a DEBRIS disc is a narrow collisional ring (Fomalhaut's is ~14 % as wide as its radius);
 * a PROTOPLANETARY disc is thick, flared and opaque in its midplane, warm at its inner rim and dark outside, with a gap
 * swept at each planet's orbit (HL Tau, PDS 70).
 */
export const DISC_LOOK: Readonly<Record<DiscKind, DiscLook>> = {
  dust_band: { color: 0xf2e6d2, g: 0.72, scatter: 0.012, warm: 0x000000, warmGain: 0, warmReach: 0, tau: 0.002, hr: 0.07, flare: 0, taper: 0.35, gapWidth: 0, gapDepth: 0, ember: 0, sheet: 0, haze: 0, albedo: 0.5 },
  debris_disc: { color: 0xe6ecf4, g: 0.45, scatter: 0.03, warm: 0x000000, warmGain: 0, warmReach: 0, tau: 0.006, hr: 0.035, flare: 0, taper: 0.22, gapWidth: 0, gapDepth: 0, ember: 0, sheet: 0, haze: 0, albedo: 0.5 },
  proto_disc: { color: 0xc8a888, g: 0.55, scatter: 0.16, warm: 0xff7a30, warmGain: 0.6, warmReach: 0.07, tau: 2.4, hr: 0.055, flare: 0.25, taper: 0.1, gapWidth: 0.085, gapDepth: 0.97, ember: 1.2, sheet: 6, haze: 0.4, albedo: 0.38 },
};
/** The disc's midplane density at radius `rM`, 0..1 (its radial profile with the gaps cut). */
export function discDensity(d: DiscSpec, rM: number): number {
  const L = DISC_LOOK[d.kind];
  const w = Math.max(1, d.outerM - d.innerM), tp = Math.max(1, w * L.taper);
  if (!(rM > 0) || !Number.isFinite(rM)) return 0;
  let rho: number;
  if (d.kind === 'debris_disc') {
    // a ring: sharp inside (the planets clear it), a longer tail outside (small grains pushed out by light)
    const peak = d.innerM + w * 0.3;
    rho = rM < peak ? Math.exp(-(((peak - rM) / (w * 0.16)) ** 2)) : Math.exp(-(((rM - peak) / (w * 0.42)) ** 2));
  } else {
    // a disc: a power law falling outward between tapered edges (the band thins as r^-1.3, the gas disc as r^-1)
    const p = d.kind === 'dust_band' ? 1.3 : 1.0;
    rho = Math.pow(d.innerM / Math.max(rM, d.innerM), p) * smooth(d.innerM - tp * 0.5, d.innerM + tp * 0.5, rM) * (1 - smooth(d.outerM - tp, d.outerM + tp * 0.4, rM));
  }
  if (L.gapWidth > 0) for (const g of d.gapsM) {
    if (!(g > 0)) continue;
    const x = (rM - g) / (g * L.gapWidth);
    rho *= 1 - L.gapDepth * Math.exp(-x * x);
  }
  return clamp(rho, 0, 1) * clamp(fin(d.strength, 1), 0, 2);
}
/** The disc's scale height (m) at radius `rM`. */
export function discScaleHeight(d: DiscSpec, rM: number): number {
  const L = DISC_LOOK[d.kind];
  return Math.max(1, rM * L.hr * Math.pow(Math.max(rM, 1) / Math.max(d.innerM, 1), L.flare));
}
/** The radii the disc's volume is bounded by (m): inside, outside, and its half-height there (3 scale heights). */
export function discBounds(d: DiscSpec): { rIn: number; rOut: number; halfH: number } {
  const L = DISC_LOOK[d.kind];
  const w = Math.max(1, d.outerM - d.innerM);
  const rOut = d.outerM + w * L.taper * 0.4, rIn = Math.max(1, d.innerM - w * L.taper * 0.5);
  return { rIn, rOut, halfH: discScaleHeight(d, rOut) * 3 };
}
/** Texels of the baked radial table (r from 0 to the outer bound). */
export const DISC_LUT_W = 512;
/**
 * The radial table the shader reads: per texel the midplane density, the scale height (÷ the outer bound's), and the
 * optical depth of the star's light along the midplane out to that radius (÷ `tauMax`, clamped).
 */
export function bakeDiscTable(d: DiscSpec, w = DISC_LUT_W): { density: Float32Array; height: Float32Array; shade: Float32Array; wide: Float32Array; rOut: number; hMax: number; kappa: number } {
  const L = DISC_LOOK[d.kind];
  const { rOut } = discBounds(d);
  const density = new Float32Array(w), height = new Float32Array(w), shade = new Float32Array(w);
  let hMax = 1;
  for (let i = 0; i < w; i++) hMax = Math.max(hMax, discScaleHeight(d, ((i + 0.5) / w) * rOut));
  // the star's light crossing the midplane: τ_r = ∫ ρ κ dr with κ set so the VERTICAL depth at the peak is `tau`
  const peakH = discScaleHeight(d, d.innerM + (d.outerM - d.innerM) * 0.2);
  const kappa = L.tau / (Math.sqrt(2 * Math.PI) * peakH);
  let tauR = 0;
  for (let i = 0; i < w; i++) {
    const r = ((i + 0.5) / w) * rOut;
    density[i] = discDensity(d, r);
    height[i] = discScaleHeight(d, r) / hMax;
    tauR += density[i] * kappa * (rOut / w);
    shade[i] = tauR;
  }
  // the same density with its gaps averaged away (a box 2.4 gap-widths across): what the ring sheet is where a gap is smaller than a pixel
  const wide = new Float32Array(w);
  for (let i = 0; i < w; i++) {
    const n = Math.max(1, Math.round(i * Math.max(L.gapWidth, 0.02) * 1.2));
    let sum = 0, c = 0;
    for (let k = Math.max(0, i - n); k <= Math.min(w - 1, i + n); k++) { sum += density[k]; c++; }
    wide[i] = sum / c;
  }
  return { density, height, shade, wide, rOut, hMax, kappa };
}
/** How the star's light thins with distance inside a disc (an exponent on r_in / r — gentler than the true inverse square, so an outer disc is seen at all). */
export const DISC_SUN_FALLOFF = 1.15;
/**
 * THE LIT SURFACE. A forming disc's haze glows where the star's light still reaches a little of it (the super-heated
 * surface layer of a flared disc — Chiang & Goldreich 1997; the midplane under it is cold and dark). The glow is weighted
 * by e^(−depth × the star's optical depth there): edge-on the midplane is a DARK LANE between two glowing surfaces
 * (HH 30), and from inside the disc the sky above and below is brighter than the plane.
 */
export const DISC_HAZE_DEPTH = 0.25;
/**
 * THE RING SHEET'S RIM. The settled grains are a smaller disc than the gas (they drift inward): the sheet's glow dies
 * over the outermost share of the radius, [start, end] as shares of the outer bound.
 */
export const DISC_EMBER_EDGE: readonly [number, number] = [0.86, 0.97];
/** The sheet is never seen shallower than this sine of the grazing angle (a zero-thickness sheet seen edge-on would be infinitely deep). */
export const DISC_SHEET_MU_MIN = 0.02;
/** How much of a forming disc's own glow is the dust's colour (starlight scattered again and again) rather than heat. */
export const DISC_EMBER_DUST = 0.45;
/** The ember's colour (linear): heat, mixed toward the dust the star lights. */
export function discEmberColor(kind: DiscKind): [number, number, number] {
  const L = DISC_LOOK[kind], w = hexLinear(L.warm), c = hexLinear(L.color), m = DISC_EMBER_DUST;
  return [w[0] * (1 - m) + c[0] * m, w[1] * (1 - m) + c[1] * m, w[2] * (1 - m) + c[2] * m];
}
/** The table clamp the half-float texture applies to the star's optical depth (the CPU mirror clamps the same). */
export const DISC_SHADE_MAX = 60;
/** Light scattered per unit of light stopped (the source function's weight): a thin band is as bright as its `scatter` says, a thick disc scatters its albedo. */
export function discScatterWeight(kind: DiscKind): number {
  const L = DISC_LOOK[kind];
  return Math.max(L.scatter, L.albedo * L.tau) / Math.max(L.tau, 1e-9);
}
/** What a march step of optical thickness `x` stops (1 − e^−x; below 1e-3 the series, where float32 loses the difference). */
export function discStepStop(x: number): number {
  const k = smooth(1e-4, 1e-3, x);
  return x * (1 - x * 0.5) * (1 - k) + (1 - Math.exp(-x)) * k;
}
/**
 * THE ORACLE: the disc shader's march in TypeScript (disc_system.discMaterial is the same arithmetic in TSL — change
 * the two TOGETHER). `ro` is the eye about the star (m, y up = the disc's axis), `rd` the unit view ray; the star's
 * light is `sun` (white at 1 unless given). Returns the linear light gathered and what the disc hides of the sky behind it.
 * ENERGY-CONSERVING STEPS: a step gives source × (1 − e^−x), never source × x — a thick disc is as bright at 8 steps
 * as at 24, and never brighter than its own source. `pxAngle` is the angle one pixel spans (rad; 0 = infinitely sharp).
 */
export function discMarchCpu(d: DiscSpec, ro: readonly [number, number, number], rd: readonly [number, number, number], steps: number, jitter = 0.5, table: ReturnType<typeof bakeDiscTable> = bakeDiscTable(d), sun: readonly [number, number, number] = [1, 1, 1], pxAngle = 0): { r: number; g: number; b: number; alpha: number } {
  const L = DISC_LOOK[d.kind];
  const { density, height, shade, wide, rOut, hMax, kappa } = table;
  const W = density.length;
  const halfH = Math.max(discBounds(d).halfH, hMax * 3);
  const col = hexLinear(L.color), warm = hexLinear(L.warm), emb = discEmberColor(d.kind);
  const sw = discScatterWeight(d.kind), g = L.g, rRef = Math.max(1, d.innerM), warmLen = Math.max(1, L.warmReach * rOut);
  // the ray inside the slab |y| ≤ halfH and the cylinder r ≤ rOut
  const ry = rd[1] + (rd[1] >= 0 ? 1e-6 : -1e-6);
  const ya = (-halfH - ro[1]) / ry, yb = (halfH - ro[1]) / ry;
  const a = rd[0] * rd[0] + rd[2] * rd[2] + 1e-9, bq = ro[0] * rd[0] + ro[2] * rd[2], cq = ro[0] * ro[0] + ro[2] * ro[2] - rOut * rOut;
  const dq = bq * bq - a * cq, sq = Math.sqrt(Math.max(dq, 0));
  const t0 = Math.max(Math.min(ya, yb), (-bq - sq) / a, 0), t1 = Math.min(Math.max(ya, yb), (-bq + sq) / a);
  const dt = dq >= 0 ? Math.max(t1 - t0, 0) / steps : 0;
  const lut = (arr: Float32Array, u: number): number => { const x = Math.min(W - 1, Math.max(0, u * W - 0.5)), i = Math.min(W - 2, Math.floor(x)), f = x - i; return arr[i] * (1 - f) + arr[i + 1] * f; };
  // THE RING SHEET: where the ray crosses the midplane (one point — the sheet is integrated, never sampled)
  const tS = -ro[1] / ry, muS = Math.max(Math.abs(rd[1]), DISC_SHEET_MU_MIN);
  const inS = L.sheet > 0 && dt > 0 && tS >= t0 && tS <= t1;
  // (the ONE step that holds the crossing, by index: a ray through the whole slab crosses the midplane exactly on a step
  //  boundary, and an interval test there finds no step, or two, by rounding)
  const iS = inS ? Math.min(steps - 1, Math.max(0, Math.floor((tS - t0) / Math.max(dt, 1e-6)))) : -1;
  let sr = 0, sg = 0, sb = 0, aS = 0;
  if (inS) {
    const rS = Math.hypot(ro[0] + rd[0] * tS, ro[2] + rd[2] * tS), uS = rS / rOut;
    // a gap smaller than a pixel is its mean (the radius one pixel spans on the sheet, against the gap's width)
    const foot = (tS * pxAngle) / muS, gw = Math.max(L.gapWidth, 0.02) * Math.max(rS, 1);
    const k = smooth(0.35 * gw, 1.2 * gw, foot), dens = lut(density, uS) * (1 - k) + lut(wide, uS) * k;
    const src = Math.pow(Math.pow(rRef / Math.max(rS, rRef), DISC_SUN_FALLOFF), 0.55) * L.ember * (1 - smooth(DISC_EMBER_EDGE[0], DISC_EMBER_EDGE[1], uS));
    aS = 1 - Math.exp((-L.sheet * dens) / muS);
    sr = emb[0] * src; sg = emb[1] * src; sb = emb[2] * src;
  }
  let ar = 0, ag = 0, ab = 0, T = 1;
  for (let i = 0; i < steps; i++) {
    const ti = t0 + i * dt, t = ti + jitter * dt;
    const px = ro[0] + rd[0] * t, py = ro[1] + rd[1] * t, pz = ro[2] + rd[2] * t;
    const r = Math.hypot(px, pz), u = r / rOut;
    const hz = py / Math.max(lut(height, u) * hMax, 1), gz = Math.exp(-0.5 * hz * hz);
    const rho = lut(density, u) * gz, tauStar = Math.min(DISC_SHADE_MAX, lut(shade, u)) * gz;
    const sunT = Math.exp(-tauStar);
    const pl = Math.max(Math.hypot(px, py, pz), 1), cosT = -(px * rd[0] + py * rd[1] + pz * rd[2]) / pl; // 1 = looking back up the light (forward scattering)
    const ph = Math.min((1 - g * g) / Math.pow(Math.max(1 + g * g - 2 * g * cosT, 1e-4), 1.5), 9);
    const fall = Math.pow(rRef / Math.max(r, rRef), DISC_SUN_FALLOFF);
    const lit = ph * fall * sunT * sw;
    const rim = Math.exp(-Math.max(r - d.innerM, 0) / warmLen) * L.warmGain, ember = Math.pow(fall, 0.55) * L.haze * Math.exp(-tauStar * DISC_HAZE_DEPTH);
    const x = rho * kappa * dt, stop = discStepStop(x);
    ar += (col[0] * sun[0] * lit + warm[0] * rim + emb[0] * ember) * stop * T; ag += (col[1] * sun[1] * lit + warm[1] * rim + emb[1] * ember) * stop * T; ab += (col[2] * sun[2] * lit + warm[2] * rim + emb[2] * ember) * stop * T;
    if (i === iS) {
      const Ts = T * Math.exp(-x * clamp((tS - ti) / Math.max(dt, 1e-6), 0, 1)); // the haze in front of the sheet inside this step
      ar += sr * aS * Ts; ag += sg * aS * Ts; ab += sb * aS * Ts;
      T *= 1 - aS;
    }
    T *= Math.exp(-x);
  }
  return { r: ar, g: ag, b: ab, alpha: 1 - T };
}
